"use client";

import { Heading } from "@/components/base/heading";
import { ContentContainer } from "@/components/layout/content-container";
import { FullWidthContainer } from "@/components/layout/full-width-container";
import { GridContainer } from "@/components/layout/grid-container";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { VisuallyHidden } from "@/components/layout/visually-hidden";
import { ContactUsEmailAndPhone } from "@/components/ui/contact-us-email-and-phone";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { HandleTextToRichText } from "@/components/ui/handle-text-to-richtext";
import { LinkButton } from "@/components/ui/link-button";
import { LinkCard } from "@/components/ui/link-card";
import {
  MediaCard,
  type MediaCardHorizontalAlignmentType,
  type MediaCardVerticalAlignmentType,
} from "@/components/ui/media-card";
import { Text } from "@/components/ui/text";
import { getMediaQuery } from "@/contexts/media-context";
import type { LandingPageFragment } from "@/generated/client.generated";
import { paths } from "@/utils/paths-utils";
import { useEffect, useState } from "react";

interface CustomerServiceParts {
  page?: LandingPageFragment;
}

export const CustomerService = ({ page }: CustomerServiceParts) => {
  const [isDesktop, setIsDesktop] = useState(false);

  //Sjekker om skjermen er desktop eller mobil
  useEffect(() => {
    const mediaQuery = window.matchMedia(getMediaQuery("md"));

    const handleResize = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsDesktop(event.matches);
    };

    mediaQuery.addEventListener("change", handleResize);
    setIsDesktop(mediaQuery.matches);

    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  return (
    <>
      <VisuallyHidden>
        <Heading type="h1">Kundeservice</Heading>
      </VisuallyHidden>
      {/* Mobil visning */}
      {!isDesktop && page?.page.responsiveMedia && (
        <FullWidthContainer spanTopMargin={true}>
          <MediaCard
            alignContentHorizontally={page.page.alignContentHorizontally as MediaCardHorizontalAlignmentType}
            alignContentVertically={page.page.alignContentVertically as MediaCardVerticalAlignmentType}
            buttonText={page.page.headerButtonText ?? undefined}
            buttonType="secondary"
            fallbackAlt="Bilde på kundeservice hovedside"
            media={page.page.responsiveMedia}
            priority={true}
            text={page.page.headerText ?? undefined}
            url={page.page.headerButtonUrl ?? ""}
            heading={page.page.headerHeading ?? undefined}
          />
          <HorizontalSpacer size="medium" />
          <ContactUsEmailAndPhone emailAddress="3t@3t.no" phoneNumber="984 20 000" showButton={false} />
        </FullWidthContainer>
      )}
      {/* Desktop visning */}
      {isDesktop && page?.page.responsiveMedia && (
        <HorizontalContainer horizontalPlacement="center" noWrap={true}>
          <ContentContainer width="xx-small">
            <HorizontalSpacer size="large" />
            <Heading type={"h1"}>Kundeservice</Heading>

            {(page?.page.description || page?.page.descriptionRichText) && (
              <>
                <HandleTextToRichText page={page.page} />
                <HorizontalSpacer />
                <LinkButton width="full" buttonType="secondary" href="mailto:3t@3t.no">
                  <Text size="large" weight="black" value="Send oss en e-post" />
                </LinkButton>
              </>
            )}

            <HorizontalSpacer size="large" />
            <HorizontalContainer horizontalPlacement="center" noWrap={true}>
              <ContactUsEmailAndPhone emailAddress="3t@3t.no" phoneNumber="984 20 000" showButton={false} />
            </HorizontalContainer>
          </ContentContainer>
          <VerticalSpacer size="large" />
          <ContentContainer width="x-small">
            <MediaCard
              buttonType="secondary"
              fallbackAlt="Bilde på kundeservice hovedside"
              media={page?.page.responsiveMedia}
              priority={true}
              rounded={true}
            />
          </ContentContainer>
        </HorizontalContainer>
      )}

      {page?.children && page?.children.length > 0 && (
        <>
          <HorizontalSpacer size="large" />
          <GridContainer columnLayout="three_columns_responsive">
            {page.children.map((child) => (
              <LinkCard
                bgColor="neutral"
                bigOnDesktop={true}
                heading={child.title}
                href={child.path}
                key={child.slug}
              />
            ))}

            <LinkCard
              bgColor="neutral"
              bigOnDesktop={true}
              heading="Medlemskap"
              href={paths.medlemskap}
              key="medlemskap"
            />
          </GridContainer>
        </>
      )}
      <HorizontalSpacer size="small" />
      <DynamicZone blocks={page?.blocks ?? []} />
    </>
  );
};
